import { useRouter } from 'next/router'
import { useState, type FC } from 'react'
import { useQueryClient } from 'react-query'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import usePenetapanDinas from 'app/KinerjaDinasPage/hooks/usePenetapanDinas'
import usePenetapanKirimDinas from 'app/KinerjaDinasPage/hooks/usePenetapanKirimDinas'
import { Header } from 'app/KinerjaShared/components/Header'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import DialogDeliveryConfirmation from 'app/KinerjaDinasPage/components/DialogDeliveryConfirmation'
import DialogDeliveryExecuting from 'app/KinerjaDinasPage/components/DialogDeliveryExecuting'
import DialogDeliverySuccess from 'app/KinerjaDinasPage/components/DialogDeliverySuccess'
import { formatDate } from 'utils/datetime'
import { IS_DEVELOPMENT } from 'app/KinerjaShared/constants/env'
import useTabKsGuru from 'app/KinerjaShared/hooks/useTabKsGuru'
import { PENGELOLAAN_KINERJA_KADIN_PATH } from 'app/KinerjaShared/constants/path'
import {
  API_GET_KS_PENILAIAN_BY_DINAS_QUERY_KEY,
  API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_QUERY_KEY,
  API_GET_KS_PENILAIAN_COUNT_BY_DINAS_QUERY_KEY,
} from 'api/kinerja/kepala-sekolah/dinas/penilaian'
import DialogDeliveryInformation from 'app/KinerjaShared/components/DialogDeliveryInformation'
import DialogDeliveryFailed from 'app/KinerjaShared/components/DialogDeliveryFailed'
import {
  API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_TAB_GURU_QUERY_KEY,
  API_GET_KS_PENILAIAN_BY_DINAS_TAB_GURU_QUERY_KEY,
} from 'api/kinerja/kepala-sekolah/guru/dinas/penilaian'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'
import usePenetapanTrackEvent from 'app/KinerjaShared/hooks/usePenetapanTrackEvent'

import type { TabFilterRoles } from 'types/kinerja-ks'

type DinasPenetapanKonfirmasiProps = {}

type AcceptableTabFilterRoles = TabFilterRoles | 'ALL'

const delay = IS_DEVELOPMENT ? 0 : 5000

const DinasPenetapanKonfirmasi: FC<DinasPenetapanKonfirmasiProps> = ({}) => {
  const router = useRouter()
  const { trackEvent } = usePenetapanTrackEvent()
  const queryClient = useQueryClient()
  const isPriorTo2025 = useIsPrior2025()
  const [isOpen, setIsOpen] = useState<{
    name:
      | 'information'
      | 'confirmation'
      | 'loading'
      | 'success'
      | 'failed'
      | null
    count?: number
    type?: AcceptableTabFilterRoles
  }>({ name: null })

  // Tabs
  const {
    render: tabs,
    activeTabName,
    setCounters: setTabCounters,
  } = useTabKsGuru({
    // Initial counters, will be updated after fetching data. Required, to display 0 when currently fetching data.
    initCounters: { KS: 0, GURU: 0 },
  })

  const onSuccess = () => {
    setIsOpen({ name: null })
    router.back()
  }

  const {
    // Kepala Sekolah
    itemsKs,
    itemsCountKs,
    refetchListKs,

    // Guru
    itemsGuru,
    itemsCountGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    isListSuccess,
    totalItems,
    hasItems,

    // Relative to active tab
    relativeItems,
    isRelativeError,
    isRelativeSuccess,
  } = usePenetapanDinas({
    activeTabName,
    status: ['BELUM_DIKIRIM'],
    confirmedOnly: true,
    onBothFetched: () => {
      // Set counters for tabs
      setTabCounters({ KS: itemsCountKs, GURU: itemsCountGuru })
    },
  })

  const { mutate } = usePenetapanKirimDinas({
    onSuccess: ({ count }) => {
      // Show success dialog and invalidate queries after 5s delay
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [API_GET_KS_PENILAIAN_COUNT_BY_DINAS_QUERY_KEY],
        })

        queryClient.invalidateQueries({
          queryKey: [API_GET_KS_PENILAIAN_BY_DINAS_QUERY_KEY],
        })

        queryClient.invalidateQueries({
          queryKey: [API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_QUERY_KEY],
        })

        queryClient.invalidateQueries({
          queryKey: [API_GET_KS_PENILAIAN_BY_DINAS_TAB_GURU_QUERY_KEY],
        })

        queryClient.invalidateQueries({
          queryKey: [API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_TAB_GURU_QUERY_KEY],
        })

        setIsOpen({ name: 'success', count })
      }, delay)

      // Send event tracker
      trackEvent({
        activityType: 'send_predikat_kinerja',
        role: 'ppk',
      })
    },
    onError: ({ type }) => {
      setIsOpen({ name: 'failed', type })
    },
  })

  // If no data can be delivered, as the user's not supposed to be here, redirect to the list page.
  if (!isListLoading && totalItems === 0 && isOpen.name === null) {
    router.replace(`${PENGELOLAAN_KINERJA_KADIN_PATH}/penetapan/kirim`)
    return null
  }

  const send = async () => {
    setIsOpen({ name: 'loading' })
    await mutate(itemsKs, itemsGuru)
  }

  return (
    <div>
      <Header
        title={
          isPriorTo2025
            ? 'Penetapan Predikat Kinerja Pegawai'
            : 'Kirim Penetapan Predikat Kinerja ke Kepala Sekolah'
        }
        className="mb-9 print:!hidden"
        onBack={() => {
          router.back()
        }}
      />

      <section className="bg-white border border-subdued rounded-lg mt-6">
        <div className="px-6 pt-6 flex items-start gap-4">
          {/* Title */}
          <div className="space-y-2.5 flex-1">
            <Text variant="heading-md">Berikut data yang akan dikirim:</Text>
            <Text color="subdued">
              Data yang tidak muncul di sini dapat Anda kirim di pengiriman
              berikutnya.
            </Text>

            <div className="bg-green-5 border border-green-50 rounded p-2 inline-block">
              <div className="flex items-center gap-2">
                <Icon color="success">check_circle_outlined</Icon>
                <Text variant="body" color="subdued">
                  Dikirim mulai dari: {formatDate(new Date())}.
                </Text>
              </div>
            </div>
          </div>

          <div className="flex-none flex flex-row gap-2.5 print:!hidden">
            {isListSuccess && (
              <>
                <Button
                  size="lg"
                  color="white"
                  type="button"
                  className="space-x-1"
                  onClick={() => window.print()}
                >
                  <Icon>print_outlined</Icon> <span>Cetak</span>
                </Button>
                <Button
                  size="lg"
                  disabled={!hasItems}
                  onClick={() => {
                    setIsOpen({ name: 'information' })
                  }}
                >
                  <Icon
                    fontSize="default"
                    color={hasItems ? 'inverse' : 'disabled'}
                    className="mr-1"
                  >
                    mail_outline
                  </Icon>
                  Konfirmasi Pengiriman ({totalItems})
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        {tabs}

        {/* Table */}
        <TablePelaksanaanKinerja className="mt-0">
          <TableHeader>
            <TableHeaderItem className="w-2/12">
              <Text variant="body" color="default" className="!font-bold">
                Nama
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Jabatan
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Rating Praktik Kinerja
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Rating Perilaku Kerja
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Predikat Kinerja
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0" />
          </TableHeader>
          <TableBody>
            {/* If loading */}
            {isListLoading && <TableShimmer columns={6} />}

            {/* If error or no data returned */}
            {!isListLoading &&
              (isRelativeError || relativeItems?.length === 0) && (
                <TableRowStates
                  colSpan={6}
                  type={isRelativeError ? 'ERROR' : 'EMPTY'}
                  onClick={() => {
                    activeTabName === 'GURU'
                      ? refetchListGuru()
                      : refetchListKs()
                  }}
                >
                  <>
                    Pastikan terdapat pegawai dengan jabatan tersebut di daerah
                    <br />
                    Anda dan sudah menyelesaikan tahap Pelaksanaan Kinerja.
                  </>
                </TableRowStates>
              )}

            {!isListLoading &&
              isRelativeSuccess &&
              hasItems &&
              relativeItems.map((item, idx) => {
                return (
                  <TableRow key={`ks_${item.npsn}_${idx}`}>
                    <TableRowItem className="align-middle">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.name || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.jabatan || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.ratingPraktikKinerja || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="body-sm"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {item?.ratingPerilakuKerja || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      {!!item.predikatKinerja ? (
                        <Text
                          variant="subheading"
                          color="informational"
                          className="overflow-wrap-anywhere uppercase !text-blue-60"
                        >
                          {item?.predikatKinerja}
                        </Text>
                      ) : (
                        <Text variant="body-sm" color="disabled">
                          Belum selesai dinilai
                        </Text>
                      )}
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0" />
                  </TableRow>
                )
              })}
          </TableBody>
        </TablePelaksanaanKinerja>
      </section>

      <DialogDeliveryInformation
        countKs={itemsCountKs}
        countGuru={itemsCountGuru}
        isOpen={isOpen.name === 'information'}
        onClickCancel={() => setIsOpen({ name: null })}
        onClickConfirm={() => setIsOpen({ name: 'confirmation' })}
      />

      <DialogDeliveryConfirmation
        isOpen={isOpen.name === 'confirmation'}
        onClickCancel={() => setIsOpen({ name: null })}
        onClickConfirm={() => send()}
      />

      <DialogDeliveryExecuting isOpen={isOpen.name === 'loading'} />

      <DialogDeliverySuccess
        count={isOpen?.count || 0}
        isOpen={isOpen.name === 'success'}
        onClick={onSuccess}
      />

      <DialogDeliveryFailed
        isOpen={isOpen.name === 'failed'}
        errorType={isOpen.type}
        onClick={onSuccess}
      />
    </div>
  )
}
export default DinasPenetapanKonfirmasi
